/**
 * ImportController — AK-368c императивный слой превью импорта на MapLibre.
 *
 * Рисует пачку контуров из файла amber-пунктиром поверх спутника (черновики,
 * ещё не поля), подсвечивает выбранную строку, приглушает пачку во время
 * «Уточнить контур» одной строки, зумит на bbox пачки.
 *
 * Изоляция (ADR-038, жёстко): свои source/layers в namespace `af-import-` — НЕ
 * трогаем fields-mvt / registry / LayerManager / measure / editor / af-draft
 * (добавления одиночного поля). Цвет amber резолвим в рантайме (resolveAmberHex,
 * MapLibre paint не ест hsl(var(--token))).
 *
 * КОНТРОЛЛЕР НЕ держит машину — её держит importStore; сюда стекают только
 * императивные команды (showPreview / highlight / dim / clear) через
 * applyImportState (re-entrancy-safe reducer, тот же паттерн, что addField).
 *
 * Все teardown-пути гейтим isMapAlive() — destroy() может выполниться после
 * map.remove() при быстрой навигации (/map → /team), как в AddFieldController.
 */
import maplibregl from 'maplibre-gl';
import { resolveAmberHex } from '../editor/earthtech';
import { isMapAlive } from '../isMapAlive';
import type { ImportGeometry } from './importApi';

const SRC = 'af-import-src';
const LAYER_FILL = 'af-import-fill';
const LAYER_LINE = 'af-import-line';
const LAYER_BAD_LINE = 'af-import-bad-line';

/** Одна фича для рендера превью: id-ключ + геометрия + флаги. */
export interface ImportRenderFeature {
  index: number;
  geometry: ImportGeometry | null;
  valid: boolean;
  selected: boolean;
}

export class ImportController {
  private amber = resolveAmberHex();
  private installed = false;

  constructor(private map: maplibregl.Map) {}

  // ── lifecycle ──────────────────────────────────────────────────────────────

  private ensureLayers(): void {
    this.amber = resolveAmberHex();
    if (!this.map.getSource(SRC)) {
      this.map.addSource(SRC, {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!this.map.getLayer(LAYER_FILL)) {
      this.map.addLayer({
        id: LAYER_FILL,
        type: 'fill',
        source: SRC,
        // Невалидные не заливаем; выбранные — плотнее.
        filter: ['==', ['get', 'valid'], true],
        paint: {
          'fill-color': this.amber,
          'fill-opacity': [
            'case',
            ['boolean', ['get', 'dim'], false],
            0.05,
            ['boolean', ['get', 'highlight'], false],
            0.3,
            ['boolean', ['get', 'selected'], false],
            0.16,
            0.08,
          ],
        },
      });
    }
    if (!this.map.getLayer(LAYER_LINE)) {
      this.map.addLayer({
        id: LAYER_LINE,
        type: 'line',
        source: SRC,
        filter: ['==', ['get', 'valid'], true],
        paint: {
          'line-color': this.amber,
          'line-width': ['case', ['boolean', ['get', 'highlight'], false], 4, 2.5],
          'line-opacity': ['case', ['boolean', ['get', 'dim'], false], 0.35, 1],
          // Пунктир = «черновик, не сохранено».
          'line-dasharray': [3, 2],
        },
      });
    }
    if (!this.map.getLayer(LAYER_BAD_LINE)) {
      // Невалидные контуры — красным пунктиром (родная destructive-палитра карты
      // резолвится не всегда; используем фиксированный «кирпичный» hex Earth Tech,
      // консистентный с fieldTypeColors/destructive на растровом слое карты).
      this.map.addLayer({
        id: LAYER_BAD_LINE,
        type: 'line',
        source: SRC,
        filter: ['==', ['get', 'valid'], false],
        paint: {
          'line-color': BAD_HEX,
          'line-width': 2,
          'line-dasharray': [2, 2],
          'line-opacity': ['case', ['boolean', ['get', 'dim'], false], 0.3, 0.9],
        },
      });
    }
    this.installed = true;
  }

  /** Полный демонтаж (удаление слоёв/source) — при destroy карты. */
  destroy(): void {
    if (!isMapAlive(this.map)) {
      this.installed = false;
      return;
    }
    for (const id of [LAYER_BAD_LINE, LAYER_LINE, LAYER_FILL]) {
      if (this.map.getLayer(id)) this.map.removeLayer(id);
    }
    if (this.map.getSource(SRC)) this.map.removeSource(SRC);
    this.installed = false;
  }

  // ── команды из reducer ──────────────────────────────────────────────────────

  /**
   * Показать/обновить пачку превью. `highlightIndex` — подсвеченная строка;
   * `dimExcept` != null — приглушить все, кроме этой (режим «Уточнить контур»:
   * редактор рисует свой td-edit слой поверх, пачка остаётся видимой тускло).
   */
  showPreview(
    features: readonly ImportRenderFeature[],
    highlightIndex: number | null,
    dimExcept: number | null,
  ): void {
    if (!isMapAlive(this.map)) return;
    this.ensureLayers();
    const src = this.map.getSource(SRC) as maplibregl.GeoJSONSource | undefined;
    if (!src) return;
    const fc: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: features
        .filter((f) => f.geometry !== null)
        // В режиме «Уточнить контур» строку dimExcept рисует РЕДАКТОР terra-draw
        // (свой td-edit слой поверх) — исключаем её из нашего оверлея, чтобы не
        // дублировать контур. Остальная пачка остаётся видимой (приглушённо).
        .filter((f) => dimExcept === null || f.index !== dimExcept)
        .map((f) => ({
          type: 'Feature',
          properties: {
            index: f.index,
            valid: f.valid,
            selected: f.selected,
            highlight: highlightIndex === f.index,
            // При активной правке одной строки — приглушаем всю остальную пачку.
            dim: dimExcept !== null,
          },
          geometry: f.geometry as unknown as GeoJSON.Geometry,
        })),
    };
    src.setData(fc);
  }

  /** Зазумить карту на bbox всех валидных геометрий пачки. */
  fitToPreview(features: readonly ImportRenderFeature[]): void {
    if (!isMapAlive(this.map)) return;
    const bounds = new maplibregl.LngLatBounds();
    let has = false;
    for (const f of features) {
      if (!f.geometry) continue;
      forEachCoord(f.geometry, (lng, lat) => {
        bounds.extend([lng, lat]);
        has = true;
      });
    }
    if (!has) return;
    try {
      this.map.fitBounds(bounds, { padding: 60, duration: 600, maxZoom: 16, essential: true });
    } catch {
      // bbox вырожден (одна точка) / карта не готова — не критично.
    }
  }

  /** Убрать оверлей превью (не трогая source-регистрацию). */
  clear(): void {
    if (!isMapAlive(this.map)) return;
    const src = this.map.getSource(SRC) as maplibregl.GeoJSONSource | undefined;
    src?.setData({ type: 'FeatureCollection', features: [] } as GeoJSON.FeatureCollection);
  }

  get isInstalled(): boolean {
    return this.installed;
  }
}

/**
 * Earth Tech «кирпичный» red для невалидных контуров превью. Императивный
 * MapLibre paint не ест hsl(var(--destructive)); фикс-hex в whitelist'е
 * src/components/map + аналог здесь (src/lib/map — map-цвета). Консистентно с
 * low-fi wireframe --red:#B4432A.
 */
const BAD_HEX = '#b4432a';

/** Обойти все координаты Polygon/MultiPolygon. */
function forEachCoord(geometry: ImportGeometry, fn: (lng: number, lat: number) => void): void {
  const walk = (arr: unknown): void => {
    if (!Array.isArray(arr)) return;
    if (typeof arr[0] === 'number' && typeof arr[1] === 'number') {
      fn(arr[0] as number, arr[1] as number);
      return;
    }
    for (const el of arr) walk(el);
  };
  walk(geometry.coordinates);
}
